import { useEffect } from 'react'
import { PLAYER_DETAILS_PARAM } from 'src/features/profile/player/playerDetails/constants/urlParams'
import { useModalParam } from 'src/hooks/useModalParam'
import type { ProfilePlayer } from '../types'

interface UsePlayerDetailsDeepLinkArgs {
  players: ProfilePlayer[]
  loadedPlayers: boolean
  selectedPlayerId: string | null
  selectPlayer: (player?: ProfilePlayer | null) => void
  /** Only a primary for the selected player may open it. */
  canEdit: boolean
}

/**
 * The profile's Player Details screen, kept in the URL
 * (`?playerDetails=<id>`) so a refresh or a shared link reopens it. A link to
 * another of the user's players selects that player first; a link to someone
 * who is not one of them is dropped.
 */
export const usePlayerDetailsDeepLink = ({
  players,
  loadedPlayers,
  selectedPlayerId,
  selectPlayer,
  canEdit,
}: UsePlayerDetailsDeepLinkArgs) => {
  'use memo'

  const param = useModalParam(PLAYER_DETAILS_PARAM)
  const linkedPlayer = param.value
    ? players.find((player) => String(player.id) === param.value)
    : undefined

  useEffect(() => {
    if (!param.value || !loadedPlayers) {
      return
    }
    if (!linkedPlayer) {
      param.close()
      return
    }
    if (String(linkedPlayer.id) !== selectedPlayerId) {
      selectPlayer(linkedPlayer)
    }
  }, [param, loadedPlayers, linkedPlayer, selectedPlayerId, selectPlayer])

  return {
    isOpen: canEdit && Boolean(param.value) && param.value === selectedPlayerId,
    open: () => {
      if (selectedPlayerId) {
        param.open(selectedPlayerId)
      }
    },
    close: param.close,
  }
}
